// src/components/storage/ThreeTierStoragePanel.js
import React, { useState, useEffect, useCallback } from "react";
import {
  Paper,
  Typography,
  Box,
  Grid,
  CircularProgress,
  Button,
  Alert,
  Chip,
  Card,
  CardContent,
  IconButton,
  Collapse,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

import RefreshIcon from "@mui/icons-material/Refresh";
import StorageIcon from "@mui/icons-material/Storage";
import ArchiveIcon from "@mui/icons-material/Archive";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SpeedIcon from "@mui/icons-material/Speed";
import BarChartIcon from "@mui/icons-material/BarChart";
import PieChartIcon from "@mui/icons-material/PieChart";

import { useEnvironment } from "../../context/EnvironmentContext";
import apiClient from "../../services/EnhancedApiClient";

const ThreeTierStoragePanel = () => {
  const { environment } = useEnvironment();
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  
  // State for storage data
  const [storageStats, setStorageStats] = useState(null);
  const [archiveStatus, setArchiveStatus] = useState(null);
  const [showMigrationDialog, setShowMigrationDialog] = useState(false);
  const [migrationLoading, setMigrationLoading] = useState(false);

  const fetchStorageData = useCallback(async () => {
    try {
      setError(null);
      
      // Fetch storage statistics and archive status in parallel
      const [statsResponse, statusResponse] = await Promise.all([
        apiClient.get('/api/admin/storage-tier-statistics'),
        apiClient.get('/api/admin/archive-service-status')
      ]);

      if (statsResponse.success) {
        setStorageStats(statsResponse.statistics);
      }

      if (statusResponse.success) {
        setArchiveStatus(statusResponse.status);
      }

    } catch (err) {
      console.error('Error fetching storage data:', err);
      setError(`Failed to load storage data: ${err.message}`);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchStorageData();
  }, [fetchStorageData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchStorageData();
  };

  const testKvStorage = async () => {
    try {
      const response = await apiClient.get("/api/admin/test-kv-storage");
      console.log("KV Storage Test Result:", response);
      alert(
        `KV Storage Test Results:\n` +
          `FRAUD_MATCH: ${response.fraudMatch.success ? "SUCCESS" : "FAILED"}\n` +
          `PAYMENT_TRACKING: ${response.paymentTracking.success ? "SUCCESS" : "FAILED"}\n\n` +
          `See console for complete details.`
      );
    } catch (error) {
      console.error("KV Storage Test Error:", error);
      alert(`KV Storage Test Error: ${error.message}`);
    }
  };

  const testKvBindings = async () => {
    try {
      const response = await apiClient.get("/api/admin/check-kv-bindings");
      console.log("KV Bindings Check Result:", response);
      alert(
        `KV Bindings Check:\n` +
          `FRAUD_MATCH: ${response.bindings.FRAUD_MATCH.exists ? "EXISTS" : "MISSING"}\n` +
          `PAYMENT_TRACKING: ${response.bindings.PAYMENT_TRACKING.exists ? "EXISTS" : "MISSING"}\n` +
          `USER_CONTEXTS: ${response.bindings.USER_CONTEXTS.exists ? "EXISTS" : "MISSING"}`
      );
    } catch (error) {
      console.error("KV Bindings Check Error:", error);
      alert(`KV Bindings Check Error: ${error.message}`);
    }
  };

  const testApiConnection = async () => {
    try {
      console.log("Testing API connection...");
      alert("API connection test started - check console for details");
    } catch (error) {
      console.error("Test connection error:", error);
      alert(`API connection error: ${error.message}`);
    }
  };

  const handleMigrationPreview = async () => {
    try {
      setMigrationLoading(true);
      const response = await apiClient.post('/api/admin/archive-migration', {
        dryRun: true,
        migrateExistingFraudWarnings: true
      });

      if (response.success) {
        const preview = response.preview || {};
        alert(`Migration Preview:\n\n` +
          `• Fraud warnings to migrate: ${preview.fraudWarningEntriesToMigrate || 0}\n` +
          `• Completed orders to process: ${preview.completedOrdersToProcess || 0}\n` +
          `• Estimated storage reduction: ${preview.estimatedReduction || 0}%`
        );
      } else {
        alert('Migration preview failed: ' + (response.error || 'Unknown error'));
      }
    } catch (err) {
      alert('Migration preview error: ' + err.message);
    } finally {
      setMigrationLoading(false);
    }
  };

  const handleMigrationExecute = async () => {
    try {
      setMigrationLoading(true);
      const response = await apiClient.post('/api/admin/archive-migration', {
        dryRun: false,
        migrateExistingFraudWarnings: true
      });

      if (response.success) {
        const results = response.results || {};
        alert(`Migration completed successfully!\n\n` +
          `• Fraud warnings migrated: ${results.fraudWarningsMigration?.migratedCount || 0}\n` +
          `• Errors: ${results.errors?.length || 0}`
        );
        
        // Refresh data after successful migration
        setShowMigrationDialog(false);
        handleRefresh();
      } else {
        alert('Migration failed: ' + (response.error || 'Unknown error'));
      }
    } catch (err) {
      alert('Migration error: ' + err.message);
    } finally {
      setMigrationLoading(false);
    }
  };

  const getStorageData = () => {
    if (!storageStats) return [];
    
    return [
      {
        name: 'Main Storage',
        count: storageStats.mainStorage?.count || 0,
        percentage: storageStats.mainStorage?.percentage || 0,
        description: 'Incomplete orders and active sessions',
        color: '#3B82F6',
        icon: <StorageIcon />
      },
      {
        name: 'Warnings Storage',
        count: storageStats.warningsStorage?.count || 0,
        percentage: storageStats.warningsStorage?.percentage || 0,
        description: 'All fraud-related data',
        color: '#EF4444',
        icon: <WarningIcon />
      },
      {
        name: 'Archive Storage',
        count: storageStats.archiveStorage?.count || 0,
        percentage: storageStats.archiveStorage?.percentage || 0,
        description: 'Clean completed orders',
        color: '#10B981',
        icon: <ArchiveIcon />
      }
    ];
  };

  return (
    <Paper id="panel-kv-storage" elevation={3} sx={{ mb: 3 }}>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          pb: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#f8f9fa",
          borderBottom: "1px solid #e9ecef",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <StorageIcon color="primary" />
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
            Fraud Warning KV Storage Monitor
          </Typography>
          <Chip
            label={environment.toUpperCase()}
            size="small"
            color={environment === "staging" ? "warning" : "primary"}
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={testKvStorage}
            sx={{
              color: "#60a5fa",
              borderColor: "#60a5fa",
              backgroundColor: "rgba(96, 165, 250, 0.1)",
              "&:hover": {
                backgroundColor: "rgba(96, 165, 250, 0.2)",
                borderColor: "#3b82f6",
              },
              fontSize: "12px",
              padding: "4px 12px",
            }}
          >
            TEST KV STORAGE
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={testKvBindings}
            sx={{
              color: "#60a5fa",
              borderColor: "#60a5fa",
              backgroundColor: "rgba(96, 165, 250, 0.1)",
              "&:hover": {
                backgroundColor: "rgba(96, 165, 250, 0.2)",
                borderColor: "#3b82f6",
              },
              fontSize: "12px",
              padding: "4px 12px",
            }}
          >
            TEST KV BINDINGS
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={testApiConnection}
            sx={{
              color: "#60a5fa",
              borderColor: "#60a5fa",
              backgroundColor: "rgba(96, 165, 250, 0.1)",
              "&:hover": {
                backgroundColor: "rgba(96, 165, 250, 0.2)",
                borderColor: "#3b82f6",
              },
              fontSize: "12px",
              padding: "4px 12px",
            }}
          >
            TEST API CONNECTION
          </Button>

          <IconButton
            size="small"
            onClick={handleRefresh}
            disabled={refreshing}
            sx={{ mr: 1 }}
          >
            <RefreshIcon
              sx={{
                animation: refreshing ? "spin 1s linear infinite" : "none",
                "@keyframes spin": {
                  "0%": { transform: "rotate(0deg)" },
                  "100%": { transform: "rotate(360deg)" },
                },
              }}
            />
          </IconButton>

          <IconButton size="small" onClick={() => setExpanded(!expanded)}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ p: 3 }}>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={4}
          >
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading storage data...</Typography>
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <>
            {/* Service Health Status */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: "center", py: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 1,
                      }}
                    >
                      {archiveStatus?.service?.healthy ? (
                        <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                      ) : (
                        <WarningIcon color="error" sx={{ mr: 1 }} />
                      )}
                      <Typography variant="h6">
                        {archiveStatus?.service?.healthy ? "Healthy" : "Issues"}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Archive Service Status
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: "center", py: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 1,
                      }}
                    >
                      <SpeedIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6">
                        {archiveStatus?.recentActivity?.archiveHealth
                          ?.successRate || "N/A"}
                        %
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Success Rate
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: "center", py: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 1,
                      }}
                    >
                      <TrendingUpIcon color="secondary" sx={{ mr: 1 }} />
                      <Typography variant="h6">
                        {archiveStatus?.recentActivity?.archiveHealth
                          ?.averageProcessingTime || "N/A"}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Processing Time
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Storage Distribution */}
            <Typography
              variant="h6"
              sx={{ mb: 2, display: "flex", alignItems: "center" }}
            >
              <BarChartIcon sx={{ mr: 1 }} />
              Storage Distribution
            </Typography>

            <Grid container spacing={2}>
              {getStorageData().map((storage, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 2 }}
                      >
                        <Box sx={{ color: storage.color, mr: 1 }}>
                          {storage.icon}
                        </Box>
                        <Typography variant="h6">{storage.name}</Typography>
                      </Box>

                      <Typography
                        variant="h4"
                        sx={{ mb: 1, color: storage.color }}
                      >
                        {storage.count.toLocaleString()}
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {storage.description}
                      </Typography>

                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Box sx={{ width: "100%", mr: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={storage.percentage}
                            sx={{
                              height: 8,
                              borderRadius: 5,
                              backgroundColor: "#e0e0e0",
                              "& .MuiLinearProgress-bar": {
                                backgroundColor: storage.color,
                              },
                            }}
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {storage.percentage}%
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Expanded Content */}
            <Collapse in={expanded}>
              <Box sx={{ mt: 3 }}>
                {/* Performance Metrics */}
                {storageStats?.performanceMetrics && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Performance Improvements
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Alert severity="success" variant="outlined">
                          <Typography variant="body2">
                            <strong>Fraud Search Optimization:</strong>
                            <br />
                            {
                              storageStats.performanceMetrics
                                .fraudSearchOptimization
                            }
                          </Typography>
                        </Alert>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Alert severity="info" variant="outlined">
                          <Typography variant="body2">
                            <strong>Archival Rate:</strong>
                            <br />
                            {storageStats.performanceMetrics.archivalRate}
                          </Typography>
                        </Alert>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Alert severity="primary" variant="outlined">
                          <Typography variant="body2">
                            <strong>Main Storage Efficiency:</strong>
                            <br />
                            {
                              storageStats.performanceMetrics
                                .mainStorageEfficiency
                            }
                          </Typography>
                        </Alert>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {/* Recent Activity */}
                {archiveStatus?.recentActivity && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Recent Activity (Last 24 Hours)
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Card variant="outlined">
                          <CardContent sx={{ textAlign: "center" }}>
                            <Typography variant="h4" color="success.main">
                              {
                                archiveStatus.recentActivity.last24Hours
                                  .ordersArchived
                              }
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Orders Archived
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Card variant="outlined">
                          <CardContent sx={{ textAlign: "center" }}>
                            <Typography variant="h4" color="error.main">
                              {
                                archiveStatus.recentActivity.last24Hours
                                  .fraudWarningsCreated
                              }
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Fraud Warnings Created
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Card variant="outlined">
                          <CardContent sx={{ textAlign: "center" }}>
                            <Typography variant="h4" color="primary.main">
                              {
                                archiveStatus.recentActivity.last24Hours
                                  .sessionsProcessed
                              }
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Sessions Processed
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {/* Migration Controls */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Migration Controls
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    <Button
                      variant="outlined"
                      onClick={handleMigrationPreview}
                      disabled={migrationLoading}
                      startIcon={<PieChartIcon />}
                    >
                      Preview Migration
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => setShowMigrationDialog(true)}
                      disabled={migrationLoading}
                      startIcon={<ArchiveIcon />}
                    >
                      Execute Migration
                    </Button>
                    <Button
                      variant="text"
                      onClick={handleRefresh}
                      disabled={refreshing}
                      startIcon={<RefreshIcon />}
                    >
                      Refresh Data
                    </Button>
                  </Box>
                </Box>

                {/* Recommendations */}
                {storageStats?.recommendations &&
                  storageStats.recommendations.length > 0 && (
                    <Alert severity="info" variant="outlined">
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Recommendations:</strong>
                      </Typography>
                      <ul style={{ margin: 0, paddingLeft: "20px" }}>
                        {storageStats.recommendations.map((rec, index) => (
                          <li key={index}>
                            <Typography variant="body2">{rec}</Typography>
                          </li>
                        ))}
                      </ul>
                    </Alert>
                  )}
              </Box>
            </Collapse>
          </>
        )}
      </Box>

      {/* Migration Confirmation Dialog */}
      <Dialog
        open={showMigrationDialog}
        onClose={() => setShowMigrationDialog(false)}
      >
        <DialogTitle>Confirm Migration</DialogTitle>
        <DialogContent>
          <Typography>
            This will migrate existing fraud warnings to the new three-tier
            storage architecture. This operation cannot be undone.
          </Typography>
          <Typography sx={{ mt: 2, fontWeight: "bold" }}>
            Are you sure you want to proceed?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMigrationDialog(false)}>Cancel</Button>
          <Button
            onClick={handleMigrationExecute}
            variant="contained"
            disabled={migrationLoading}
          >
            {migrationLoading ? "Migrating..." : "Execute Migration"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ThreeTierStoragePanel;